import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { 
  Globe, Plus, Save, Trash2, Star, Edit2, X, Check, 
  ChevronDown, ChevronUp, AlertCircle, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';
import FlagIcon from '../../components/FlagIcon';

const API = process.env.REACT_APP_BACKEND_URL;

// Common flags for quick selection
const COMMON_FLAGS = [
  { code: 'pl', flag: '🇵🇱', name: 'Polish', nativeName: 'Polski' },
  { code: 'en', flag: '🇬🇧', name: 'English', nativeName: 'English' },
  { code: 'de', flag: '🇩🇪', name: 'German', nativeName: 'Deutsch' },
  { code: 'uk', flag: '🇺🇦', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'fr', flag: '🇫🇷', name: 'French', nativeName: 'Français' },
  { code: 'es', flag: '🇪🇸', name: 'Spanish', nativeName: 'Español' },
  { code: 'it', flag: '🇮🇹', name: 'Italian', nativeName: 'Italiano' },
  { code: 'cs', flag: '🇨🇿', name: 'Czech', nativeName: 'Čeština' },
  { code: 'sk', flag: '🇸🇰', name: 'Slovak', nativeName: 'Slovenčina' },
  { code: 'ru', flag: '🇷🇺', name: 'Russian', nativeName: 'Русский' },
];

const AdminLanguages = () => {
  const { token } = useAdminAuth();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  
  // New language form state
  const [newLanguage, setNewLanguage] = useState({
    code: '',
    name: '',
    nativeName: '',
    flag: '🏳️',
    isActive: true,
    urlMappings: {}
  });

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/languages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLanguages(response.data);
    } catch (error) {
      console.error('Error fetching languages:', error);
      toast.error('Błąd podczas pobierania języków');
    } finally {
      setLoading(false);
    }
  };

  const initializeLanguages = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/api/admin/languages/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Języki zainicjalizowane pomyślnie');
      fetchLanguages();
    } catch (error) {
      if (error.response?.data?.error?.includes('already initialized')) {
        toast.info('Języki są już zainicjalizowane');
      } else {
        toast.error('Błąd podczas inicjalizacji języków');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddLanguage = async () => {
    if (!newLanguage.code || !newLanguage.name || !newLanguage.nativeName) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/api/admin/languages`, newLanguage, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Język dodany pomyślnie');
      setShowAddForm(false);
      setNewLanguage({ code: '', name: '', nativeName: '', flag: '🏳️', isActive: true, urlMappings: {} });
      fetchLanguages();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas dodawania języka');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLanguage = async (codeOrId, updates) => {
    // Use code as identifier since it's more stable
    const identifier = typeof codeOrId === 'object' ? codeOrId.code : codeOrId;
    try {
      setSaving(true);
      await axios.put(`${API}/api/admin/languages/${identifier}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Język zaktualizowany');
      fetchLanguages();
      setEditingId(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas aktualizacji');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLanguage = async (codeOrId) => {
    const identifier = typeof codeOrId === 'object' ? codeOrId.code : codeOrId;
    if (!window.confirm('Czy na pewno chcesz usunąć ten język?')) return;

    try {
      setSaving(true);
      await axios.delete(`${API}/api/admin/languages/${identifier}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Język usunięty');
      fetchLanguages();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas usuwania');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (codeOrId) => {
    const identifier = typeof codeOrId === 'object' ? codeOrId.code : codeOrId;
    await handleUpdateLanguage(identifier, { isDefault: true });
  };

  const handleToggleActive = async (codeOrId, currentState) => {
    const identifier = typeof codeOrId === 'object' ? codeOrId.code : codeOrId;
    await handleUpdateLanguage(identifier, { isActive: !currentState });
  };

  const selectPresetLanguage = (preset) => {
    setNewLanguage({
      ...newLanguage,
      code: preset.code,
      name: preset.name,
      nativeName: preset.nativeName,
      flag: preset.flag
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Języki</h1>
          <p className="text-gray-400">Zarządzaj dostępnymi językami i tłumaczeniami URL</p>
        </div>
        <div className="flex gap-2">
          {languages.length === 0 && (
            <Button
              onClick={initializeLanguages}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Globe className="w-4 h-4 mr-2" />
              Zainicjalizuj PL/EN
            </Button>
          )}
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Dodaj język
          </Button>
        </div>
      </div>

      {/* Add Language Form */}
      {showAddForm && (
        <Card className="mb-4 bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Dodaj nowy język
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick select presets */}
            <div>
              <Label className="text-gray-400 text-sm mb-2 block">Szybki wybór:</Label>
              <div className="flex flex-wrap gap-2">
                {COMMON_FLAGS.filter(f => !languages.find(l => l.code === f.code)).map(preset => (
                  <Button
                    key={preset.code}
                    variant="outline"
                    size="sm"
                    onClick={() => selectPresetLanguage(preset)}
                    className="border-white/20 hover:bg-white/10"
                  >
                    <span className="mr-1"><FlagIcon code={preset.code} size={18} /></span>
                    {preset.nativeName}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-400">Kod (2-5 znaków) *</Label>
                <Input
                  value={newLanguage.code}
                  onChange={(e) => setNewLanguage({ ...newLanguage, code: e.target.value.toLowerCase() })}
                  placeholder="np. de, uk"
                  maxLength={5}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Nazwa (EN) *</Label>
                <Input
                  value={newLanguage.name}
                  onChange={(e) => setNewLanguage({ ...newLanguage, name: e.target.value })}
                  placeholder="German"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Nazwa natywna *</Label>
                <Input
                  value={newLanguage.nativeName}
                  onChange={(e) => setNewLanguage({ ...newLanguage, nativeName: e.target.value })}
                  placeholder="Deutsch"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Flaga (emoji)</Label>
                <Input
                  value={newLanguage.flag}
                  onChange={(e) => setNewLanguage({ ...newLanguage, flag: e.target.value })}
                  placeholder="🇩🇪"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setNewLanguage({ code: '', name: '', nativeName: '', flag: '🏳️', isActive: true, urlMappings: {} });
                }}
                className="border-white/20"
              >
                <X className="w-4 h-4 mr-2" />
                Anuluj
              </Button>
              <Button
                onClick={handleAddLanguage}
                disabled={saving}
                className="bg-primary hover:bg-primary/90"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Dodaj
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Languages List */}
      {languages.length === 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-500" />
            <p className="text-gray-400 mb-4">Brak skonfigurowanych języków</p>
            <Button onClick={initializeLanguages} disabled={saving}>
              Zainicjalizuj domyślne języki (PL/EN)
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {languages.map(language => (
            <Card key={language.code} className="mb-4 bg-white/5 border-white/10">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <FlagIcon code={language.code} size={32} />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">{language.nativeName}</h3>
                        <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-gray-400 uppercase">
                          {language.code}
                        </span>
                        {language.isDefault && (
                          <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Domyślny
                          </span>
                        )}
                        {!language.isActive && (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                            Nieaktywny
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{language.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Aktywny</span>
                      <Switch
                        checked={language.isActive}
                        onCheckedChange={() => handleToggleActive(language.code, language.isActive)}
                        disabled={saving || (language.isActive && languages.filter(l => l.isActive).length <= 1)}
                      />
                    </div>
                    
                    {!language.isDefault && language.isActive && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetDefault(language.code)}
                        disabled={saving}
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                      >
                        <Star className="w-4 h-4 mr-1" />
                        Ustaw domyślny
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedId(expandedId === language.code ? null : language.code)}
                      className="border-white/20"
                    >
                      {expandedId === language.code ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                      URL Mappings
                    </Button>

                    {!language.isDefault && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteLanguage(language.code)}
                        disabled={saving}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* URL Mappings (expanded) */}
                {expandedId === language.code && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Mapowania URL (SEO)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(language.urlMappings || {}).map(([key, value]) => (
                        <div key={key} className="bg-white/5 rounded p-2">
                          <Label className="text-xs text-gray-500 block mb-1">{key}</Label>
                          <div className="flex gap-2 items-center">
                            <span className="text-xs text-gray-400">/{language.code}/</span>
                            <Input
                              value={value || ''}
                              onChange={(e) => {
                                const newMappings = { ...language.urlMappings, [key]: e.target.value };
                                setLanguages(prev => prev.map(l => 
                                  l.code === language.code 
                                    ? { ...l, urlMappings: newMappings }
                                    : l
                                ));
                              }}
                              className="h-7 text-xs bg-white/5 border-white/10 flex-1"
                              placeholder={key}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            setSaving(true);
                            await axios.put(`${API}/api/admin/languages/${language.code}`, {
                              urlMappings: language.urlMappings
                            }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Mapowania URL zapisane');
                          } catch (error) {
                            console.error('Error saving URL mappings:', error);
                            toast.error('Błąd podczas zapisywania mapowań URL');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        disabled={saving}
                        className="bg-primary hover:bg-primary/90"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Zapisz mapowania
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Instructions */}
      <Card className="bg-blue-500/10 border-blue-500/20">
        <CardContent className="py-4">
          <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Jak dodać nowy język?
          </h4>
          <ol className="text-sm text-gray-400 space-y-1 list-decimal list-inside">
            <li>Kliknij "Dodaj język" i wybierz z predefiniowanych lub wpisz własne dane</li>
            <li>Po dodaniu języka, przejdź do "Tłumaczenia" aby przetłumaczyć treści z FitNet</li>
            <li>Tłumaczenia UI (menu, przyciski) są w kodzie - skontaktuj się z developerem</li>
            <li>URL mappings są generowane automatycznie (można je zmienić w bazie)</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLanguages;
